/**
 * Closed-case history: what it is allowed to say, and what it must never.
 *
 * Every other signal in this product describes something that is *open*. This
 * one describes cases that ended, and it is the only column here that could be
 * read as good news — which is exactly why the wording is pinned in a module
 * with tests rather than written inline, the treatment `alerts.ts` and
 * `milestone.ts` already get.
 *
 * **A closed case is not a repair.** The county publishes no disposition: a
 * case closes because the work was done, because it was superseded by another
 * case, or because the county stopped pursuing it, and nothing in the feed
 * tells those apart. So this may say "closed" and may never say fixed,
 * resolved, remediated, repaired or cleared — every one of which is a claim
 * about a named building that nobody made.
 *
 * **And zero is not silence.** `closed_cases === 0` means the county register
 * covers this parcel and holds nothing closed for it; `null` means the
 * register does not reach it at all — which is every county but Miami-Dade,
 * and every municipality inside Miami-Dade. Rendering them alike would turn
 * "we cannot see" into "there is nothing", which is the difference this
 * product exists to keep.
 */

/** The county register's window, in years. Mirrors `CLOSED_WINDOW_YEARS`. */
export const CLOSED_WINDOW_YEARS = 5;

/**
 * The one-line summary, or "" when there is nothing we may claim.
 *
 * Returns "" for null on purpose: no row, no chip, no dash. A dash reads as a
 * measurement that came back empty, and we have not measured anything.
 */
export function closedCaseLabel(
  cases: number | null | undefined,
  medianDays: number | null | undefined,
): string {
  if (cases === null || cases === undefined) return "";
  if (cases === 0) {
    return `None closed in ${CLOSED_WINDOW_YEARS} years`;
  }
  const noun = cases === 1 ? "case" : "cases";
  const label = `${cases.toLocaleString()} ${noun} closed in ${CLOSED_WINDOW_YEARS} years`;
  return medianDays === null || medianDays === undefined
    ? label
    : `${label} · median ${formatDuration(medianDays)}`;
}

/**
 * How long a case took, in a unit a person reads.
 *
 * Days up to three months, then months, then years — because "1,435 days"
 * is arithmetic and "3.9 years" is the fact that a case sat open across four
 * of them. Rounded rather than truncated, and never to zero: a case closed
 * the day it opened took "same day", not "0 days".
 */
export function formatDuration(days: number): string {
  if (days <= 0) return "same day";
  if (days < 90) return `${Math.round(days)} days`;
  if (days < 730) return `${Math.round(days / 30)} months`;
  return `${(days / 365).toFixed(1)} years`;
}

/**
 * The sentence explaining what the number is, for a tooltip or a caption.
 *
 * Names the register rather than the product, because the claim is the
 * county's: we are reporting what its own record holds, and where it holds
 * nothing we are reporting that too.
 */
export function closedCaseBasis(cases: number | null | undefined): string {
  if (cases === null || cases === undefined) return "";
  return (
    `County building-violation cases closed on the parcels we hold here, ` +
    `in the county's rolling ${CLOSED_WINDOW_YEARS}-year window. A closed ` +
    `case is not a record of repair — the register does not say why a case ` +
    `ended.`
  );
}


/**
 * Permit activity, worded so it cannot be read as a repair.
 *
 * The register records that work was *permitted*, not what the work was for
 * and not that it was done. A permit issued after an unsafe-structure case may
 * be a new air conditioner — so this reports a date and a sequence, and the
 * word "repair" appears nowhere in it.
 *
 * `false` and `null` are different again: false means we held two dates and
 * the permit was the older one, null means there was nothing to compare.
 */
export function permitActivityLabel(
  lastPermitAt: string | null | undefined,
  afterCase: boolean | null | undefined,
): string {
  if (!lastPermitAt) return "";
  const when = formatPermitDate(lastPermitAt);
  return afterCase
    ? `Permit issued ${when}, after the latest case`
    : `Permit issued ${when}`;
}

/**
 * A permit date for the UI. Unparseable input yields "".
 *
 * Parsed by hand rather than with `new Date(value)`, which reads a bare
 * `YYYY-MM-DD` as **UTC midnight** and then renders it in the viewer's zone —
 * so a permit issued on the 1st of a month displays as the previous month for
 * everyone west of Greenwich, which is everyone reading this product. The
 * county publishes a calendar date, not an instant, and it stays one.
 *
 * This is `epoch_to_date`'s warning arriving on the client: the same shift, in
 * the other direction, on a value that has no time at all.
 */
export function formatPermitDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return "";
  const [, year, month] = match;
  const index = Number(month) - 1;
  if (index < 0 || index > 11) return "";
  return `${MONTHS[index]} ${year}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
